import React, { useEffect, useState, useRef, lazy } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { MarketplaceHeader } from '../components/layout/MarketplaceHeader';
import { Footer } from '../components/layout/Footer';
import { RestaurantHeader } from '../components/restaurant/RestaurantHeader';
import { MenuItemCard } from '../components/restaurant/MenuItemCard';
import { MenuCategoryTabs } from '../components/restaurant/MenuCategoryTabs';
import { ProductDetailModal } from '../components/restaurant/ProductDetailModal';
import { FloatingCartButton } from '../components/restaurant/FloatingCartButton';
import { useRestaurantBySlug, useMenuData, useMarketplaceData } from '../lib/hooks';
import { useCart } from '../lib/cart';
import { EmptyState } from '../components/ui/EmptyState';
import { Store, Utensils, ChevronRight, Plus, ChevronDown } from 'lucide-react';
import { MenuItem, CartItemOption } from '../lib/types';
import { formatCurrency } from '../lib/utils';
import { useKitchenPause } from '../lib/kitchen-pause-context';
// Mexican-inspired gradient colors for category cards
const MEXICAN_GRADIENTS = ['linear-gradient(135deg, #006847 0%, #2AABB3 100%)', 'linear-gradient(135deg, #CE1126 0%, #E8751A 100%)', 'linear-gradient(135deg, #E8751A 0%, #F4D35E 100%)', 'linear-gradient(135deg, #6B2FA0 0%, #CE1126 100%)', 'linear-gradient(135deg, #2AABB3 0%, #006847 100%)', 'linear-gradient(135deg, #C9A34E 0%, #E8751A 100%)', 'linear-gradient(135deg, #CE1126 0%, #6B2FA0 100%)', 'linear-gradient(135deg, #006847 0%, #C9A34E 100%)'];
export function RestaurantPage() {
  const {
    slug
  } = useParams<{
    slug: string;
  }>();
  const navigate = useNavigate();
  const {
    restaurant,
    settings,
    loading: restaurantLoading,
    error
  } = useRestaurantBySlug(slug);
  const {
    categories,
    items,
    loading: menuLoading
  } = useMenuData(restaurant?.id);
  const {
    zones
  } = useMarketplaceData();
  const {
    addItem
  } = useCart();
  const {
    isKitchenPaused,
    monitorRestaurant
  } = useKitchenPause();
  const [selectedProduct, setSelectedProduct] = useState<MenuItem | null>(null);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [activeCategory, setActiveCategory] = useState<string>('');
  const menuSectionRef = useRef<HTMLDivElement>(null);
  // Register restaurant with central kitchen pause monitor
  useEffect(() => {
    if (restaurant?.id) {
      console.log('[NOIZA][RestaurantPage] Registering restaurant:', restaurant.id, 'slug:', slug);
      monitorRestaurant(restaurant.id);
    }
  }, [restaurant?.id, monitorRestaurant]);
  // Set initial active category when categories load
  useEffect(() => {
    if (categories.length > 0 && !activeCategory) {
      setActiveCategory(categories[0].id);
    }
  }, [categories, activeCategory]);
  const handleProductClick = (item: MenuItem) => {
    setSelectedProduct(item);
    setIsModalOpen(true);
  };
  const handleAddToCart = (item: MenuItem, quantity: number, options: CartItemOption[], notes: string) => {
    if (!restaurant) return;
    if (isKitchenPaused) {
      alert('La cocina está pausada en este momento. Vuelve en 10 minutos.');
      return;
    }
    const success = addItem({
      menu_item_id: item.id,
      restaurant_id: restaurant.id,
      item_name_snapshot: item.name,
      unit_price_snapshot: item.price,
      quantity,
      notes,
      image_url: item.image_url,
      selected_options: options,
      station_code: item.station_code || null
    }, restaurant.name);
    if (!success) {
      alert('Ya tienes productos de otro restaurante en tu carrito. Por favor, finaliza o vacía tu pedido actual primero.');
    }
  };
  // Scroll to a category section within the page
  const scrollToCategory = (categoryId: string) => {
    setActiveCategory(categoryId);
    const element = document.getElementById(`category-${categoryId}`);
    if (element) {
      const y = element.getBoundingClientRect().top + window.scrollY - 140;
      window.scrollTo({
        top: y,
        behavior: 'smooth'
      });
    }
  };
  // Scroll to the full menu section
  const scrollToMenu = () => {
    if (menuSectionRef.current) {
      const y = menuSectionRef.current.getBoundingClientRect().top + window.scrollY - 80;
      window.scrollTo({
        top: y,
        behavior: 'smooth'
      });
    }
  };
  // Loading state — dark
  if (restaurantLoading) {
    return <div className="min-h-screen flex flex-col bg-[#0B0B0B]" data-id="element-2559">
        <MarketplaceHeader data-id="element-2560" />
        <div className="h-48 sm:h-64 w-full bg-[#141414] animate-pulse" data-id="element-2561" />
        <div className="max-w-5xl mx-auto px-4 w-full mt-8" data-id="element-2562">
          <div className="h-10 w-1/3 mb-4 bg-[#1a1a1a] rounded-lg animate-pulse" data-id="element-2563" />
          <div className="h-4 w-2/3 mb-8 bg-[#1a1a1a] rounded animate-pulse" data-id="element-2564" />
          <div className="grid grid-cols-2 md:grid-cols-2 gap-3" data-id="element-2565">
            {[1, 2, 3, 4].map(i => <div key={i} className="h-40 rounded-2xl bg-[#1a1a1a] animate-pulse" data-id="element-2566" />)}
          </div>
        </div>
      </div>;
  }
  // Error state — dark
  if (error || !restaurant) {
    return <div className="min-h-screen flex flex-col bg-[#0B0B0B]" data-id="element-2567">
        <MarketplaceHeader data-id="element-2568" />
        <div className="flex-1 flex items-center justify-center" data-id="element-2569">
          <EmptyState icon={Store} title="Restaurante no encontrado" description="El restaurante que buscas no existe o no está disponible en este momento." actionLabel="Volver al inicio" onAction={() => navigate('/')} data-id="element-2570" />
        </div>
        <Footer data-id="element-2571" />
      </div>;
  }
  const zoneName = zones.find(z => z.id === restaurant.zone_id)?.name;
  const highlightedItems = items.slice(0, 4);
  const primaryColor = restaurant.primary_color || '#F59E0B';
  return <div className="min-h-screen flex flex-col bg-[#0B0B0B] pb-24" data-id="element-2572">
      <MarketplaceHeader data-id="element-2573" />

      <main className="flex-1" data-id="element-2574">
        <RestaurantHeader restaurant={restaurant} settings={settings} zoneName={zoneName} data-id="element-2575" />

        <div className="max-w-5xl mx-auto px-3 sm:px-6 lg:px-8 py-8 sm:py-12" data-id="element-2576">
          {/* Highlighted Items — compact horizontal rows */}
          {highlightedItems.length > 0 && <section className="mb-8 sm:mb-12" data-id="element-2577">
              <div className="flex items-center justify-between mb-3 sm:mb-4" data-id="element-2578">
                <h2 className="text-xl sm:text-2xl font-black text-white" data-id="element-2579">
                  ⭐ Destacados
                </h2>
              </div>
              <div className="flex flex-col gap-2" data-id="element-2580">
                {highlightedItems.map(item => <div key={item.id} onClick={() => handleProductClick(item)} className="flex items-center gap-3 bg-[#141414] border border-white/5 rounded-xl p-2 cursor-pointer active:opacity-80 transition-opacity" data-id="element-2581">
                    <img src={item.image_url || 'https://images.unsplash.com/photo-1504674900247-0877df9cc836?w=300&h=300&fit=crop&q=60'} alt={item.name} className="w-16 h-16 rounded-lg object-cover shrink-0" loading="lazy" data-id="element-2582" />
                    <div className="flex-1 min-w-0" data-id="element-2583">
                      <h3 className="font-bold text-white text-sm leading-tight line-clamp-1" data-id="element-2584">
                        {item.name}
                      </h3>
                      {item.description && <p className="text-[11px] text-white/40 line-clamp-1 mt-0.5" data-id="element-2585">
                          {item.description}
                        </p>}
                      <span className="font-bold text-[#F4D35E] text-sm mt-0.5 block" data-id="element-2586">
                        {formatCurrency(item.price)}
                      </span>
                    </div>
                    <button className="w-8 h-8 rounded-full flex items-center justify-center bg-[#006847] text-white shrink-0 active:scale-90 transition-transform" onClick={e => {
                e.stopPropagation();
                handleProductClick(item);
              }} data-id="element-2587">
                      <Plus className="w-4 h-4" data-id="element-2588" />
                    </button>
                  </div>)}
              </div>
            </section>}

          {/* Categories Quick Nav — dark cards with Mexican gradients */}
          <section className="mb-8 sm:mb-12" data-id="element-2589">
            <h2 className="text-xl sm:text-2xl font-black text-white mb-4 sm:mb-6" data-id="element-2590">
              🔥 Categorías
            </h2>
            {menuLoading ? <div className="grid grid-cols-3 sm:grid-cols-4 gap-3" data-id="element-2591">
                {[1, 2, 3, 4].map(i => <div key={i} className="h-24 rounded-2xl bg-[#1a1a1a] animate-pulse" data-id="element-2592" />)}
              </div> : categories.length > 0 ? <div className="grid grid-cols-3 sm:grid-cols-4 gap-3" data-id="element-2593">
                {categories.map((category, idx) => <button key={category.id} onClick={() => scrollToCategory(category.id)} className="relative overflow-hidden p-4 sm:p-5 rounded-2xl text-center transition-transform active:scale-95 text-left" style={{
              background: MEXICAN_GRADIENTS[idx % MEXICAN_GRADIENTS.length]
            }} data-id="element-2594">
                    {/* Subtle overlay for depth */}
                    <div className="absolute inset-0 bg-black/20 hover:bg-black/10 transition-colors" data-id="element-2595" />
                    <div className="relative z-10" data-id="element-2596">
                      <div className="w-10 h-10 mx-auto rounded-full flex items-center justify-center mb-2 bg-white/20" data-id="element-2597">
                        <Utensils className="w-5 h-5 text-white" data-id="element-2598" />
                      </div>
                      <h3 className="font-bold text-white text-xs sm:text-sm leading-tight text-center" data-id="element-2599">
                        {category.name}
                      </h3>
                    </div>
                  </button>)}
              </div> : <p className="text-white/40" data-id="element-2600">No hay categorías disponibles.</p>}
          </section>

          {/* CTA to scroll to full menu */}
          <div className="text-center p-6 sm:p-8 rounded-2xl border border-white/10 relative overflow-hidden mb-10" style={{
          background: 'linear-gradient(135deg, #006847 0%, #0B0B0B 50%, #CE1126 100%)'
        }} data-id="element-2601">
            <div className="absolute inset-0 bg-black/60" data-id="element-2602" />
            <div className="relative z-10" data-id="element-2603">
              <h3 className="text-lg sm:text-xl font-black text-white mb-2" data-id="element-2604">
                ¿Listo para ordenar? 🌮
              </h3>
              <p className="text-white/60 text-sm mb-1" data-id="element-2605">
                Explora el menú completo de{' '}
                <span className="font-bold text-white" data-id="element-2606">{restaurant.name}</span>{' '}
                y personaliza tu pedido.
              </p>
              <p className="text-xs text-white/30 mb-5" data-id="element-2607">
                Tu pedido irá directamente a este restaurante por WhatsApp.
              </p>
              <button onClick={scrollToMenu} className="inline-flex items-center gap-2 px-8 py-3 rounded-full font-bold text-[#0B0B0B] transition-colors active:scale-95" style={{
              background: `linear-gradient(135deg, ${primaryColor}, #C9A34E)`
            }} data-id="element-2608">
                Ver Menú Completo <ChevronDown className="w-5 h-5" data-id="element-2609" />
              </button>
            </div>
          </div>

          {/* ═══ FULL MENU SECTION — inline, same page ═══ */}
          <div ref={menuSectionRef} data-id="element-2610">
            {categories.length > 0 && <>
                {/* Sticky category tabs */}
                <MenuCategoryTabs categories={categories} activeCategory={activeCategory} onSelect={scrollToCategory} primaryColor={primaryColor} data-id="element-2611" />

                {/* Menu items grouped by category */}
                <div className="mt-6 space-y-10" data-id="element-2612">
                  {categories.map(category => {
                const categoryItems = items.filter(item => item.category_id === category.id);
                if (categoryItems.length === 0) return null;
                return <section key={category.id} id={`category-${category.id}`} className="scroll-mt-40" data-id="element-2613">
                        <h3 className="text-lg sm:text-xl font-black text-white mb-4 flex items-center gap-2" data-id="element-2614">
                          <span className="w-1.5 h-6 rounded-full bg-[#F4D35E]" data-id="element-2615" />
                          {category.name}
                        </h3>
                        <div className="grid grid-cols-1 sm:grid-cols-2 gap-3" data-id="element-2616">
                          {categoryItems.map(item => <MenuItemCard key={item.id} item={item} onClick={() => handleProductClick(item)} primaryColor={primaryColor} data-id="element-2617" />)}
                        </div>
                      </section>;
              })}
                </div>
              </>}

            {/* If no categories but items exist, show all items */}
            {categories.length === 0 && items.length > 0 && <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 mt-6" data-id="element-2618">
                {items.map(item => <MenuItemCard key={item.id} item={item} onClick={() => handleProductClick(item)} primaryColor={primaryColor} data-id="element-2619" />)}
              </div>}
          </div>
        </div>
      </main>

      <FloatingCartButton primaryColor={primaryColor} data-id="element-2620" />

      <ProductDetailModal isOpen={isModalOpen} onClose={() => setIsModalOpen(false)} item={selectedProduct} onAdd={handleAddToCart} primaryColor={primaryColor} data-id="element-2621" />

      <Footer data-id="element-2622" />
    </div>;
}